<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :baseZIndex="1200"
    :closable="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '30rem' }"
    :breakpoints="{ '960px': '92vw', '575px': '98vw' }"
    :pt="crcFormDialogPtNoScroll"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div class="crc-form-dialog-header__icon bg-sky-50 dark:bg-sky-500/15 text-sky-600 dark:text-sky-400">
          <i class="pi pi-sitemap text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            {{ modalTitle }}
          </div>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <section class="crc-form-dialog-section crc-form-dialog-fields">
        <FloatLabel variant="on" class="w-full">
          <InputText
            id="subdept-edit-name"
            v-model="draft.name"
            :disabled="saving"
            maxlength="160"
            autocomplete="off"
            :invalid="submitted && !draft.name?.trim()"
            class="w-full rounded-md"
          />
          <label for="subdept-edit-name" class="text-sm font-medium">
            Name <span class="text-red-500">*</span>
          </label>
        </FloatLabel>
        <small v-if="submitted && !draft.name?.trim()" class="text-xs text-red-500 pl-1 -mt-2">
          Please enter a sub-department name.
        </small>

        <QuickAddSelectField
          v-model="draft.department_id"
          input-id="subdept-edit-department"
          label="Department"
          :required="true"
          :options="departmentOptions"
          :disabled="saving || !canSetDepartment"
          :invalid="submitted && !draft.department_id"
          invalid-message="Please select a department."
          :can-add="canAddDepartment && canSetDepartment"
          add-label="Create New Department"
          create-placeholder="New department name"
          :max-length="120"
          :create-option="createDepartmentOption"
          @update:model-value="onDepartmentChange"
        />

        <QuickAddSelectField
          v-model="draft.parent_id"
          input-id="subdept-edit-parent"
          label="Parent sub-department"
          :options="parentOptions"
          show-clear
          :disabled="saving || !draft.department_id"
          :can-add="false"
        />
      </section>

      <section
        v-if="mode === 'edit'"
        class="crc-form-dialog-section"
      >
        <div class="crc-form-dialog-status">
          <div>
            <div class="crc-form-dialog-status__title">Active</div>
            <div class="crc-form-dialog-status__hint">
              Inactive sub-departments are hidden from profile options.
            </div>
          </div>
          <ToggleSwitch v-model="draft.is_active" :disabled="saving" />
        </div>
      </section>

      <div class="crc-form-dialog-footer">
        <div class="crc-form-dialog-footer__actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="visibleProxy = false"
          />
          <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import ToggleSwitch from 'primevue/toggleswitch';
import QuickAddSelectField from '@/components/forms/QuickAddSelectField.vue';
import { crcFormDialogPtNoScroll } from '@/components/common/dialog/crcDialogPt.js';
import { useAuth } from '@/composables/useAuth';
import { useQuickAddReference } from '@/features/org-references/composables/useQuickAddReference';
import { buildQuickAddPolicy } from '@/features/org-references/config/referencePermissions';
import { normalizeReferenceId } from '@/features/org-references/utils/referenceOptionUtils';

defineOptions({ name: 'SubDepartmentEditModal' });

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  departments: {
    type: Array,
    default: () => [],
  },
  subDepartments: {
    type: Array,
    default: () => [],
  },
  canSetDepartment: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:visible', 'save']);

const mode = ref('new');
const submitted = ref(false);

const draft = reactive({
  id: null,
  name: '',
  department_id: null,
  parent_id: null,
  is_active: true,
});

const { isSuperAdmin, hasPermission } = useAuth();
const canAddDepartment = computed(
  () => buildQuickAddPolicy({ hasPermission, isSuperAdmin: isSuperAdmin.value }).department
);

const departmentQuickAdd = useQuickAddReference({
  entity: 'department',
  options: computed(() => props.departments),
  onCreated(option) {
    draft.department_id = normalizeReferenceId(option.id);
    onDepartmentChange();
  },
});
const createDepartmentOption = departmentQuickAdd.create;

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
});

const departmentOptions = computed(() =>
  Array.isArray(props.departments) ? props.departments : []
);

const parentOptions = computed(() => {
  if (!draft.department_id) return [];
  const deptId = Number(draft.department_id);
  return (props.subDepartments || []).filter(
    (item) =>
      Number(item.department_id) === deptId &&
      Number(item.id) !== Number(draft.id)
  );
});

const modalTitle = computed(() =>
  mode.value === 'new' ? 'New Sub-department' : 'Edit Sub-department'
);

const resetDraft = () => {
  draft.id = null;
  draft.name = '';
  draft.department_id = null;
  draft.parent_id = null;
  draft.is_active = true;
  submitted.value = false;
  mode.value = 'new';
};

const fillDraft = (item) => {
  draft.id = item?.id ?? null;
  draft.name = item?.name || '';
  draft.department_id = item?.department_id ?? null;
  draft.parent_id = item?.parent_id ?? null;
  draft.is_active = item?.is_active !== false;
  submitted.value = false;
};

const onDepartmentChange = () => {
  if (draft.parent_id) {
    const parent = parentOptions.value.find((item) => item.id === draft.parent_id);
    if (!parent) {
      draft.parent_id = null;
    }
  }
};

const onClose = () => {
  if (!props.saving) {
    resetDraft();
  }
};

const handleSubmit = () => {
  submitted.value = true;
  if (!draft.name?.trim() || !draft.department_id) return;

  const payload = {
    name: draft.name.trim(),
    department_id: draft.department_id,
    parent_id: draft.parent_id ?? null,
  };

  if (mode.value === 'edit' && draft.id) {
    payload.sub_department_id = draft.id;
    payload.is_active = draft.is_active ? 1 : 0;
  }

  emit('save', { mode: mode.value, payload });
};

defineExpose({
  openNew: (preset = {}) => {
    resetDraft();
    mode.value = 'new';
    if (preset?.department_id != null) {
      draft.department_id = normalizeReferenceId(preset.department_id);
    }
    if (preset?.parent_id != null) {
      draft.parent_id = normalizeReferenceId(preset.parent_id);
    }
  },
  openEdit: (item) => {
    mode.value = 'edit';
    fillDraft(item);
  },
});
</script>
